import { useEffect, useState } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import {
  getSessions,
  createSession,
  updateSession,
  deleteSession,
  bulkUpdateSessions,
  getClients,
  type ClientSession,
  type Client,
} from '../../../services/api'

const springConfig = { type: "spring", stiffness: 60, damping: 10 }

export default function DashboardSessions() {
  const [loading, setLoading] = useState(true)
  const [sessions, setSessions] = useState<(ClientSession & { clients?: { name: string; email: string } })[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [selectedSessions, setSelectedSessions] = useState<Set<string>>(new Set())
  const [isFormOpen, setIsFormOpen] = useState(false)
  const [editingSession, setEditingSession] = useState<ClientSession | null>(null)
  const [saving, setSaving] = useState(false)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [serviceTypeFilter, setServiceTypeFilter] = useState<string>('all')
  const [formData, setFormData] = useState({
    client_id: '',
    booking_id: '',
    payment_id: '',
    service_type: 'tutoring' as 'tutoring' | 'web-dev',
    session_type: '',
    title: '',
    description: '',
    scheduled_date: '',
    duration_minutes: 60,
    status: 'scheduled' as 'scheduled' | 'in-progress' | 'completed' | 'canceled',
    notes: '',
    progress_notes: '',
    next_steps: '',
    rating: undefined as number | undefined,
    feedback: '',
  })

  useEffect(() => {
    loadData()
  }, [statusFilter, serviceTypeFilter])

  useEffect(() => {
    loadClients()
  }, [])

  const loadData = async () => {
    setLoading(true)
    try {
      const filters: any = {}
      if (statusFilter !== 'all') filters.status = statusFilter
      if (serviceTypeFilter !== 'all') filters.service_type = serviceTypeFilter

      const data = await getSessions(filters)
      setSessions(data as any)
    } catch (error) {
      console.error('Error loading sessions:', error)
    } finally {
      setLoading(false)
    }
  }

  const loadClients = async () => {
    const data = await getClients()
    setClients(data)
  }

  const handleCreate = () => {
    setEditingSession(null)
    setFormData({
      client_id: '',
      booking_id: '',
      payment_id: '',
      service_type: 'tutoring',
      session_type: '',
      title: '',
      description: '',
      scheduled_date: '',
      duration_minutes: 60,
      status: 'scheduled',
      notes: '',
      progress_notes: '',
      next_steps: '',
      rating: undefined,
      feedback: '',
    })
    setIsFormOpen(true)
  }

  const handleEdit = (session: ClientSession) => {
    setEditingSession(session)
    setFormData({
      client_id: session.client_id,
      booking_id: session.booking_id || '',
      payment_id: session.payment_id || '',
      service_type: session.service_type,
      session_type: session.session_type || '',
      title: session.title,
      description: session.description || '',
      scheduled_date: session.scheduled_date ? new Date(session.scheduled_date).toISOString().slice(0, 16) : '',
      duration_minutes: session.duration_minutes || 60,
      status: session.status,
      notes: session.notes || '',
      progress_notes: session.progress_notes || '',
      next_steps: session.next_steps || '',
      rating: session.rating,
      feedback: session.feedback || '',
    })
    setIsFormOpen(true)
  }

  const handleSave = async () => {
    if (!formData.client_id || !formData.title) {
      alert('Please fill in required fields (Client and Title)')
      return
    }

    setSaving(true)
    try {
      const sessionData: any = {
        ...formData,
        scheduled_date: formData.scheduled_date ? new Date(formData.scheduled_date).toISOString() : null,
        booking_id: formData.booking_id || null,
        payment_id: formData.payment_id || null,
      }

      if (editingSession) {
        await updateSession(editingSession.id, sessionData)
      } else {
        await createSession(sessionData)
      }

      setIsFormOpen(false)
      loadData()
    } catch (error: any) {
      alert(error.message || 'Failed to save session')
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this session?')) return

    try {
      await deleteSession(id)
      loadData()
    } catch (error) {
      alert('Failed to delete session')
    }
  }

  const handleBulkAction = async (action: 'delete' | 'status', value?: string) => {
    if (selectedSessions.size === 0) {
      alert('Please select at least one session')
      return
    }

    if (action === 'delete') {
      if (!confirm(`Are you sure you want to delete ${selectedSessions.size} session(s)?`)) return
      for (const id of selectedSessions) {
        await deleteSession(id)
      }
    } else if (action === 'status' && value) {
      await bulkUpdateSessions(Array.from(selectedSessions), { status: value as any })
    }

    setSelectedSessions(new Set())
    loadData()
  }

  const toggleSelect = (id: string) => {
    const newSelected = new Set(selectedSessions)
    if (newSelected.has(id)) {
      newSelected.delete(id)
    } else {
      newSelected.add(id)
    }
    setSelectedSessions(newSelected)
  }

  const toggleSelectAll = () => {
    if (selectedSessions.size === filteredSessions.length) {
      setSelectedSessions(new Set())
    } else {
      setSelectedSessions(new Set(filteredSessions.map(s => s.id)))
    }
  }

  const filteredSessions = sessions.filter(session => {
    const matchesSearch = !searchTerm || 
      session.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.clients?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      session.clients?.email?.toLowerCase().includes(searchTerm.toLowerCase())
    return matchesSearch
  })

  const formatDate = (dateString?: string) => {
    if (!dateString) return 'Not scheduled'
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    })
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'text-green-600 dark:text-green-400'
      case 'in-progress':
        return 'text-blue-600 dark:text-blue-400'
      case 'scheduled':
        return 'text-yellow-600 dark:text-yellow-400'
      case 'canceled':
        return 'text-red-600 dark:text-red-400'
      default:
        return 'text-text-primary/70'
    }
  }

  if (loading) {
    return (
      <div className="text-center py-12">
        <div className="text-4xl mb-4">⏳</div>
        <p className="text-text-primary/70">Loading sessions...</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h3 className="text-xl sm:text-2xl font-semibold text-text-primary mb-1">Sessions</h3>
          <p className="text-sm text-text-primary/70">Manage completed work sessions and milestones</p>
        </div>
        <motion.button
          onClick={handleCreate}
          className="px-4 sm:px-6 py-2.5 sm:py-3 neu rounded-xl font-semibold text-text-primary neu-hover bg-accent/10 hover:bg-accent/20 transition-colors"
          whileHover={{ scale: 1.02, y: -1 }}
          whileTap={{ scale: 0.98 }}
          transition={springConfig}
        >
          + New Session
        </motion.button>
      </div>

      {/* Filters and Search */}
      <div className="neu p-4 rounded-2xl space-y-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <input
            type="text"
            placeholder="Search by title, client name, or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          />
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          >
            <option value="all">All Statuses</option>
            <option value="scheduled">Scheduled</option>
            <option value="in-progress">In Progress</option>
            <option value="completed">Completed</option>
            <option value="canceled">Canceled</option>
          </select>
          <select
            value={serviceTypeFilter}
            onChange={(e) => setServiceTypeFilter(e.target.value)}
            className="px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
          >
            <option value="all">All Services</option>
            <option value="tutoring">Tutoring</option>
            <option value="web-dev">Web Development</option>
          </select>
        </div>

        {/* Bulk Actions */}
        {selectedSessions.size > 0 && (
          <div className="flex items-center gap-2 pt-2 border-t border-shadow-dark/10">
            <span className="text-sm text-text-primary/70">
              {selectedSessions.size} selected
            </span>
            <select
              onChange={(e) => {
                if (e.target.value) {
                  handleBulkAction('status', e.target.value)
                  e.target.value = ''
                }
              }}
              className="px-3 py-1.5 neu rounded-lg text-sm text-text-primary bg-background focus:outline-none"
            >
              <option value="">Bulk Status Update</option>
              <option value="scheduled">Set to Scheduled</option>
              <option value="in-progress">Set to In Progress</option>
              <option value="completed">Set to Completed</option>
              <option value="canceled">Set to Canceled</option>
            </select>
            <motion.button
              onClick={() => handleBulkAction('delete')}
              className="px-3 py-1.5 neu rounded-lg text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20"
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
            >
              Delete Selected
            </motion.button>
          </div>
        )}
      </div>

      {/* Sessions List */}
      {filteredSessions.length === 0 ? (
        <div className="neu p-12 rounded-2xl text-center">
          <p className="text-text-primary/70">No sessions found.</p>
        </div>
      ) : (
        <div className="space-y-4">
          <div className="neu rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-background/50">
                  <tr>
                    <th className="px-4 py-3 text-left">
                      <input
                        type="checkbox"
                        checked={selectedSessions.size === filteredSessions.length && filteredSessions.length > 0}
                        onChange={toggleSelectAll}
                        className="neu rounded"
                      />
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Title</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Client</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Service</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Duration</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Status</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-primary">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSessions.map((session) => (
                    <tr
                      key={session.id}
                      className="border-t border-shadow-dark/10 hover:bg-background/30"
                    >
                      <td className="px-4 py-3">
                        <input
                          type="checkbox"
                          checked={selectedSessions.has(session.id)}
                          onChange={() => toggleSelect(session.id)}
                          className="neu rounded"
                        />
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary font-medium">
                        {session.title}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary">
                        {session.clients?.name || session.clients?.email || 'N/A'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70 capitalize">
                        {session.service_type}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70">
                        {formatDate(session.scheduled_date)}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-primary/70">
                        {session.duration_minutes ? `${session.duration_minutes} min` : 'N/A'}
                      </td>
                      <td className="px-4 py-3">
                        <span className={`text-xs font-medium px-2 py-1 rounded ${getStatusColor(session.status)}`}>
                          {session.status}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex gap-2">
                          <button
                            onClick={() => handleEdit(session)}
                            className="text-accent hover:text-accent-secondary text-sm"
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(session.id)}
                            className="text-red-600 hover:text-red-700 text-sm"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      <AnimatePresence>
        {isFormOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm"
            onClick={() => setIsFormOpen(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9 }}
              className="neu rounded-2xl p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <h3 className="text-xl font-semibold mb-4 text-text-primary">
                {editingSession ? 'Edit Session' : 'New Session'}
              </h3>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Client *
                  </label>
                  <select
                    value={formData.client_id}
                    onChange={(e) => setFormData({ ...formData, client_id: e.target.value })}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    required
                  >
                    <option value="">Select a client</option>
                    {clients.map((client) => {
                      const displayName = client.first_name && client.last_name ? `${client.first_name} ${client.last_name}` : client.name || client.email.split('@')[0];
                      return (
                        <option key={client.id} value={client.id}>
                          {displayName} ({client.email})
                        </option>
                      );
                    })}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Title *
                  </label>
                  <input
                    type="text"
                    value={formData.title}
                    onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    required
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Service Type
                    </label>
                    <select
                      value={formData.service_type}
                      onChange={(e) => setFormData({ ...formData, service_type: e.target.value as any })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="tutoring">Tutoring</option>
                      <option value="web-dev">Web Development</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Status
                    </label>
                    <select
                      value={formData.status}
                      onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    >
                      <option value="scheduled">Scheduled</option>
                      <option value="in-progress">In Progress</option>
                      <option value="completed">Completed</option>
                      <option value="canceled">Canceled</option>
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Scheduled Date
                    </label>
                    <input
                      type="datetime-local"
                      value={formData.scheduled_date}
                      onChange={(e) => setFormData({ ...formData, scheduled_date: e.target.value })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2 text-text-primary/70">
                      Duration (minutes)
                    </label>
                    <input
                      type="number"
                      value={formData.duration_minutes}
                      onChange={(e) => setFormData({ ...formData, duration_minutes: parseInt(e.target.value) || 60 })}
                      className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                      min="1"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Description
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    rows={3}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2 text-text-primary/70">
                    Notes
                  </label>
                  <textarea
                    value={formData.notes}
                    onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                    rows={2}
                    className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                  />
                </div>

                {formData.status === 'completed' && (
                  <>
                    <div>
                      <label className="block text-sm font-medium mb-2 text-text-primary/70">
                        Progress Notes
                      </label>
                      <textarea
                        value={formData.progress_notes}
                        onChange={(e) => setFormData({ ...formData, progress_notes: e.target.value })}
                        rows={3}
                        className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium mb-2 text-text-primary/70">
                          Rating (1-5)
                        </label>
                        <input
                          type="number"
                          value={formData.rating || ''}
                          onChange={(e) => setFormData({ ...formData, rating: e.target.value ? parseInt(e.target.value) : undefined })}
                          className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30"
                          min="1"
                          max="5"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium mb-2 text-text-primary/70">
                        Feedback
                      </label>
                      <textarea
                        value={formData.feedback}
                        onChange={(e) => setFormData({ ...formData, feedback: e.target.value })}
                        rows={2}
                        className="w-full px-4 py-2.5 neu rounded-xl text-text-primary bg-background focus:outline-none focus:ring-2 focus:ring-accent/30 resize-none"
                      />
                    </div>
                  </>
                )}
              </div>

              <div className="flex justify-end gap-4 mt-6">
                <motion.button
                  onClick={() => setIsFormOpen(false)}
                  className="px-4 py-2.5 neu rounded-xl font-medium text-text-primary/70 hover:text-text-primary"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Cancel
                </motion.button>
                <motion.button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-4 py-2.5 neu rounded-xl font-semibold text-text-primary neu-hover bg-accent/10 hover:bg-accent/20 disabled:opacity-50"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  {saving ? 'Saving...' : 'Save'}
                </motion.button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  )
}

